// Démo « Fondations & institutions » : reproduit le tableau de bord Fondation réel
// (mêmes composants), avec des données entièrement fictives et sans accès aux données.
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDemoPage } from "@/lib/useDemoPage";
import { BRAND_NAME } from "@/config/brand";
import ExhibitionsPanel, { exhibitionStatus } from "../foundation/ExhibitionsPanel";
import {
  CollectionCard, LoanOutCard, StatBox, StatBoxContrats, EmptyState, sBtn,
} from "../../pages/FoundationDashboard";

const inDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const COLLECTIONS = [
  {
    id: "c1", title: "Collection permanente — peintures XVIIIe et XIXe", collection_type: "foundation_owned",
    legal_status: null, legal_notes: "Inscrite à l'inventaire de la fondation depuis sa création.",
    artworkCount: 214, totalValue: 6850000, deposit: null,
  },
  {
    id: "c2", title: "Fonds du fondateur", collection_type: "foundation_founder",
    legal_status: "pret_institution", legal_notes: null,
    artworkCount: 38, totalValue: 2100000, deposit: null,
  },
  {
    id: "c3", title: "Legs Marin-Delacroix", collection_type: "foundation_donation",
    legal_status: "don_legs_inalienable", legal_notes: "Legs de 2019 : œuvres inaliénables, conditions d'exposition précisées à l'acte.",
    artworkCount: 57, totalValue: 1480000, deposit: null,
  },
  {
    id: "c4", title: "Dépôt de la collection Vasseur", collection_type: "foundation_deposit",
    legal_status: null, legal_notes: null, artworkCount: 24, totalValue: 940000,
    deposit: {
      owner_type: "private_collector", owner_name: "Famille Vasseur (fictif)",
      contract_start_date: inDays(-1090), contract_end_date: inDays(38),
      insurance_required: true, insurance_holder: "Déposant",
    },
  },
  {
    id: "c5", title: "Dépôt de l'association des amis", collection_type: "foundation_deposit",
    legal_status: null, legal_notes: null, artworkCount: 11, totalValue: 310000,
    deposit: {
      owner_type: "institution", owner_name: "Association des amis (fictif)",
      contract_start_date: inDays(-200), contract_end_date: inDays(520),
      insurance_required: false, insurance_holder: null,
    },
  },
];

const LOANS_OUT = [
  { id: "l1", title: "Paysage d'automne", artist: "École de Barbizon", inventory_number: "INV-0148", storage_location_detail: "Prêt à un musée de région, retour prévu dans 4 mois", collections: { title: "Collection permanente — peintures XVIIIe et XIXe" } },
  { id: "l2", title: "Portrait de jeune femme", artist: "Atelier français", inventory_number: "INV-0061", storage_location_detail: "Exposition itinérante", collections: { title: "Legs Marin-Delacroix" } },
  { id: "l3", title: "Nature morte à la carafe", artist: "École flamande", inventory_number: "INV-0203", storage_location_detail: "Prêt pour restauration partagée", collections: { title: "Fonds du fondateur" } },
];

// Fiches d'œuvres fictives, par collection (mêmes rubriques que la fiche œuvre réelle).
const art = (id, title, artist, year, medium, dimensions, inventory, location, status, condition, checkDaysAgo, value, acquisition, provenance, documents) =>
  ({ id, title, artist, year, medium, dimensions, inventory, location, status, condition, checkDaysAgo, value, acquisition, provenance, documents });

const SHEETS = {
  c1: [
    art("s1", "Scène galante", "École française", "XVIIIe siècle", "Huile sur toile", "92 × 73 cm", "INV-0102", "Grand Salon, mur est", "Exposée", "Bon état général", 40, 45000, "Achat en vente publique, 1994", "Collection privée parisienne, puis vente publique en 1994.", ["Facture d'achat", "Rapport d'état 2025"]),
    art("s2", "Portrait de magistrat", "Atelier de province", "XVIIIe siècle", "Huile sur toile", "81 × 65 cm", "INV-0107", "Grand Salon, mur nord", "Exposée", "Restauration recommandée (vernis)", 95, 28000, "Achat, 1998", "Provenance à documenter avant 1998.", ["Facture d'achat"]),
    art("s3", "Marine au crépuscule", "École du Nord", "XIXe siècle", "Huile sur toile", "60 × 90 cm", "INV-0131", "Réserve, rack 4", "En réserve", "Excellent état", 20, 36000, "Achat, 2003", "Ancienne collection de fondation, catalogue de vente 2003.", ["Facture d'achat", "Certificat d'authenticité"]),
    art("s4", "Le Bouquet", "École française", "circa 1860", "Huile sur panneau", "46 × 38 cm", "INV-0156", "Salon rouge", "Exposée", "Bon état général", 60, 12000, "Achat, 2011", "Vente publique à Paris, 2011.", ["Facture d'achat"]),
  ],
  c2: [
    art("s5", "Nature morte à la carafe", "École flamande", "XVIIe siècle", "Huile sur toile", "65 × 81 cm", "INV-0203", "En prêt", "En prêt", "Bon état, contrôle avant départ", 130, 32000, "Prêt du fondateur, 2015", "Propriété du fondateur, prêtée à la fondation.", ["Contrat de prêt"]),
    art("s6", "Étude de mains", "Atelier parisien", "circa 1920", "Fusain sur papier", "42 × 29,7 cm", "INV-0219", "Cabinet des arts graphiques", "En réserve", "Léger jaunissement du papier", 75, 2400, "Prêt du fondateur, 2015", "Propriété du fondateur.", ["Contrat de prêt"]),
  ],
  c3: [
    art("s7", "Portrait de jeune femme", "Atelier français", "XIXe siècle", "Huile sur toile", "55 × 46 cm", "INV-0061", "En prêt", "En prêt", "Bon état général", 110, 22000, "Legs Marin-Delacroix, 2019", "Legs de 2019 : œuvre inaliénable.", ["Acte de legs", "Rapport d'état 2024"]),
    art("s8", "Académie", "École française", "XIXe siècle", "Dessin à la sanguine", "58 × 42 cm", "INV-0064", "Cabinet des arts graphiques", "Exposée", "Bon état général", 30, 4800, "Legs Marin-Delacroix, 2019", "Legs de 2019 : œuvre inaliénable.", ["Acte de legs"]),
    art("s9", "Croquis de nu", "Atelier parisien", "circa 1900", "Mine de plomb", "30 × 21 cm", "INV-0070", "Cabinet des arts graphiques", "En réserve", "Excellent état", 15, 1600, "Legs Marin-Delacroix, 2019", "Legs de 2019 : œuvre inaliénable.", ["Acte de legs"]),
  ],
  c4: [
    art("s10", "Buste d'homme au manteau", "Atelier du XIXe siècle", "circa 1870", "Terre cuite", "45 × 32 × 28 cm", "DEP-0011", "Galerie des sculptures", "Exposée (dépôt)", "Fissure mineure à surveiller", 25, 9500, "Dépôt famille Vasseur, 2022", "Propriété du déposant.", ["Contrat de dépôt", "Attestation d'assurance"]),
    art("s11", "Paysage de campagne", "École de Barbizon", "circa 1855", "Huile sur panneau", "32 × 41 cm", "DEP-0014", "Réserve, rack 2", "En réserve (dépôt)", "Excellent état", 50, 6500, "Dépôt famille Vasseur, 2022", "Propriété du déposant.", ["Contrat de dépôt"]),
  ],
  c5: [
    art("s12", "Scène mythologique", "D'après Poussin", "XIXe siècle", "Huile sur toile", "195 × 145 cm", "DEP-0031", "Grand Salon", "Exposée (dépôt)", "Excellent état", 45, 48000, "Dépôt de l'association, 2025", "Propriété de l'association.", ["Contrat de dépôt"]),
  ],
};

const loanSheet = (a) =>
  art(a.id, a.title, a.artist, "—", "Huile sur toile", "—", a.inventory_number, a.storage_location_detail, "En prêt", "Contrôle à prévoir au retour", 90, null, "—", "—", ["Convention de prêt"]);

const EXHIBITIONS = [
  { id: "e1", title: "Regards sur la collection", exhibition_type: "temporary", venue: "Grand Salon", curator: "Équipe de conservation", description: "Une sélection de peintures du XVIIIe siècle, mise en dialogue avec des œuvres du legs.", start_date: inDays(-45), end_date: inDays(70) },
  { id: "e2", title: "Parcours permanent", exhibition_type: "permanent", venue: "Rez-de-chaussée", curator: null, description: null, start_date: inDays(-900), end_date: null },
  { id: "e3", title: "Dessins et études", exhibition_type: "temporary", venue: "Cabinet des arts graphiques", curator: null, description: null, start_date: inDays(35), end_date: inDays(130) },
  { id: "e4", title: "Hommage au fondateur", exhibition_type: "temporary", venue: "Galerie basse", curator: null, description: null, start_date: inDays(-260), end_date: inDays(-120) },
];
const EXHIBITION_ITEMS = { e1: ["a1", "a2", "a3", "a4", "a5", "a6"], e2: ["a1", "a2", "a3", "a4", "a5", "a6", "a7", "a8"], e3: ["a7", "a8", "a9"], e4: ["a3", "a4"] };
const ARTWORKS = [
  ["a1", "Scène galante"], ["a2", "Portrait de magistrat"], ["a3", "Marine au crépuscule"], ["a4", "Le Bouquet"],
  ["a5", "Vue de village"], ["a6", "Paysage d'automne"], ["a7", "Étude de mains"], ["a8", "Académie"], ["a9", "Croquis de nu"],
].map(([id, title]) => ({ id, title, artist: "Fictif" }));

const TABS = [
  { key: "owned", label: "Propriété propre", types: ["foundation_owned", "foundation_founder"] },
  { key: "donation", label: "Donations & Legs", types: ["foundation_donation"] },
  { key: "deposit", label: "Dépôts & Prêts reçus", types: ["foundation_deposit"] },
  { key: "loans", label: "Prêts sortants", types: null },
  { key: "expositions", label: "Expositions", types: null },
];

const daysUntil = (d) => (d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null);
const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const TEAM = [
  { name: "Claire Aubry (fictive)", role: "Utilisateur Family Office", access: "Lecture et modification" },
  { name: "Marc Lefèvre (fictif)", role: "Utilisateur Family Office", access: "Lecture seule" },
  { name: "Cabinet Delorme Art Conseil (fictif)", role: "Art Advisor", access: "Lecture seule" },
];

const REPORT_SECTIONS = [
  "1. Synthèse patrimoniale, avec la répartition par collection",
  "2. Inventaire par collection",
  "3. État de conservation",
  "4. Conventions et tiers",
  "5. Note du directeur",
  "6. Certification : signature du directeur et date de soumission au Conseil",
];

function DemoPanel({ title, onClose, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex" }}>
      <button type="button" aria-label="Fermer" onClick={onClose}
        style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", cursor: "pointer", border: "none" }} />
      <div style={{ position: "relative", marginLeft: "auto", width: "min(520px, 100vw)", background: "#fff", height: "100%", overflowY: "auto", padding: "28px 24px", boxShadow: "-4px 0 24px rgba(0,0,0,0.12)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontFamily: "Georgia, serif", fontSize: 20 }}>{title}</span>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function DemoInstitution() {
  const trackCta = useDemoPage("institution");
  const navigate = useNavigate();
  const [tab, setTab] = useState("owned");
  const [panel, setPanel] = useState(null);
  const [openCol, setOpenCol] = useState(null);
  const [openArt, setOpenArt] = useState(null);

  const totalArtworks = COLLECTIONS.reduce((s, c) => s + c.artworkCount, 0);
  const totalValue = COLLECTIONS.reduce((s, c) => s + c.totalValue, 0);
  const expiring = useMemo(
    () => COLLECTIONS.filter((c) => c.collection_type === "foundation_deposit" && (daysUntil(c.deposit?.contract_end_date) ?? 0) <= 60),
    []
  );
  const ongoing = EXHIBITIONS.filter((e) => exhibitionStatus(e) === "ongoing");
  const visible = COLLECTIONS.filter((c) => TABS.find((t) => t.key === tab)?.types?.includes(c.collection_type));

  return (
    <div style={{ minHeight: "100vh", background: "#FAFAF8" }}>
      <div style={{ background: "#4F46E5", color: "#fff", padding: "12px 16px", textAlign: "center" }}>
        <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>
          Voici le tableau de bord d'une fondation, avec des données fictives. Aucune donnée réelle n'est affichée.{" "}
          <button
            onClick={() => { trackCta("demander_demo_personnalisee"); navigate("/contact?plan=fondation&demo=true"); }}
            style={{ color: "#fff", textDecoration: "underline", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, padding: 0 }}
          >
            Demander une démonstration personnalisée
          </button>
        </p>
      </div>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "40px 24px" }}>
        <div style={{ marginBottom: 28, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontFamily: "Georgia, serif", fontSize: 28, color: "#1a1a1a", margin: "0 0 4px" }}>Tableau de bord Fondation</h1>
            <p style={{ fontSize: 13, color: "#888", fontFamily: "system-ui", margin: 0 }}>Gestion des collections et des contrats de dépôt</p>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <button type="button" onClick={() => { trackCta("rapport_annuel"); setPanel("report"); }} style={sBtn("#1a1a1a", "#fff")}>Rapport annuel</button>
            <button type="button" onClick={() => { trackCta("collaborateurs"); setPanel("team"); }} style={sBtn("#fff", "#1a1a1a", "#e0d8cc")}>Collaborateurs</button>
            <button type="button" onClick={() => { trackCta("depots_prets"); setTab("deposit"); }} style={sBtn("#fff", "#1a1a1a", "#e0d8cc")}>Dépôts & Prêts →</button>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 32 }}>
          <StatBox label="Collections" value={COLLECTIONS.length} />
          <StatBox label="Œuvres actives" value={totalArtworks} sub="+12 ce mois" />
          <StatBox label="Valeur estimée" value={fmt(totalValue)} sub="+8,2 % vs il y a 1 an (41 œuvres)" />
          <StatBox label="Expositions en cours" value={ongoing.length} sub={`${ongoing.filter((e) => e.exhibition_type === "temporary").length} temporaire(s) · ${ongoing.filter((e) => e.exhibition_type === "permanent").length} permanente(s)`} />
          <StatBoxContrats count={expiring.length} list={expiring} onNavigate={() => setTab("deposit")} />
          <StatBox label="Prêts sortants" value={LOANS_OUT.length} />
        </div>

        <div style={{ display: "flex", borderBottom: "1px solid #e0d8cc", marginBottom: 28, overflowX: "auto" }}>
          {TABS.map((t) => {
            const count = t.key === "expositions" ? EXHIBITIONS.length : t.types ? COLLECTIONS.filter((c) => t.types.includes(c.collection_type)).length : LOANS_OUT.length;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: "10px 18px", background: "none", border: "none", whiteSpace: "nowrap",
                borderBottom: tab === t.key ? "2px solid #1a1a1a" : "2px solid transparent", cursor: "pointer",
                fontSize: 13, fontFamily: "system-ui", color: tab === t.key ? "#1a1a1a" : "#888", fontWeight: tab === t.key ? 600 : 400,
                display: "flex", alignItems: "center", gap: 6,
              }}>
                {t.label}
                <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10, background: "#f0ece5", color: "#888", border: "1px solid #e0d8cc" }}>{count}</span>
              </button>
            );
          })}
        </div>

        {tab !== "loans" && tab !== "expositions" && (visible.length === 0
          ? <EmptyState icon="◇" title="Aucune collection" text="Créez une collection avec le type fondation correspondant pour la gérer ici." />
          : visible.map((c) => <CollectionCard key={c.id} col={c} demo onOpen={(col) => { trackCta("voir_collection"); setOpenCol(col); }} />))}

        {tab === "loans" && (
          <>
            <div style={{ padding: "12px 16px", background: "#eef2f8", border: "1px solid #c5d4e8", borderRadius: 6, marginBottom: 16, fontSize: 13, color: "#1c3a5e", fontFamily: "system-ui" }}>
              Ces œuvres sont actuellement prêtées à des institutions tierces. Pensez à vérifier les dates de retour directement sur chaque fiche.
            </div>
            {LOANS_OUT.map((a) => <LoanOutCard key={a.id} artwork={a} demo onOpen={(x) => { trackCta("fiche_pret"); setOpenArt({ sheet: loanSheet(x), from: null }); }} />)}
          </>
        )}

        {tab === "expositions" && (
          <ExhibitionsPanel readOnly exhibitions={EXHIBITIONS} itemsByExhibition={EXHIBITION_ITEMS} artworks={ARTWORKS} onReload={() => {}} />
        )}

        {openCol && !openArt && (
          <DemoPanel title={openCol.title} onClose={() => setOpenCol(null)}>
            <p style={{ fontSize: 13, color: "#6b6b6b", fontFamily: "system-ui", marginTop: 0 }}>
              {openCol.artworkCount} œuvres au total. Voici un extrait de la collection : ouvrez une fiche.
            </p>
            {(SHEETS[openCol.id] || []).map((s) => (
              <button key={s.id} type="button" onClick={() => { trackCta("fiche_oeuvre"); setOpenArt({ sheet: s, from: openCol }); }}
                style={{ display: "block", width: "100%", textAlign: "left", background: "#fff", border: "1px solid #e0d8cc", borderRadius: 6, padding: "12px 14px", marginBottom: 8, cursor: "pointer", fontFamily: "system-ui" }}>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 15, color: "#1a1a1a" }}>{s.title}</div>
                <div style={{ fontSize: 12, color: "#888" }}>{s.artist} · {s.year} · {s.inventory}</div>
                <div style={{ fontSize: 11, color: "#1c3a5e", marginTop: 2 }}>{s.status}</div>
              </button>
            ))}
          </DemoPanel>
        )}
        {openArt && (
          <DemoPanel title="Fiche œuvre" onClose={() => { setOpenArt(null); setOpenCol(null); }}>
            {openArt.from && (
              <button type="button" onClick={() => setOpenArt(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#888", fontFamily: "system-ui", padding: 0, marginBottom: 12 }}>
                ← Retour à la collection
              </button>
            )}
            <div style={{ height: 150, background: "#f3f0ea", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "#b9b2a4", fontSize: 13, fontFamily: "system-ui", marginBottom: 14 }}>
              Photographie de l'œuvre
            </div>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, margin: "0 0 2px" }}>{openArt.sheet.title}</h2>
            <div style={{ fontSize: 14, color: "#666", fontFamily: "system-ui", marginBottom: 14 }}>{openArt.sheet.artist}{openArt.sheet.year && openArt.sheet.year !== "—" ? `, ${openArt.sheet.year}` : ""}</div>
            {[
              ["Identification", [["Technique", openArt.sheet.medium], ["Dimensions", openArt.sheet.dimensions], ["N° d'inventaire", openArt.sheet.inventory]]],
              ["Emplacement", [["Lieu", openArt.sheet.location], ["Statut", openArt.sheet.status]]],
              ["Conservation", [["État", openArt.sheet.condition], ["Dernier contrôle", new Date(Date.now() - openArt.sheet.checkDaysAgo * 86400000).toLocaleDateString("fr-FR")]]],
              ["Valeur", [["Estimation indicative", openArt.sheet.value ? fmt(openArt.sheet.value) : "À établir"]]],
              ["Provenance et acquisition", [["Acquisition", openArt.sheet.acquisition], ["Provenance", openArt.sheet.provenance]]],
            ].map(([h, rows]) => (
              <div key={h} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8b7355", fontWeight: 700, fontFamily: "system-ui", marginBottom: 4 }}>{h}</div>
                {rows.map(([k, v]) => (
                  <div key={k} style={{ display: "flex", gap: 12, fontSize: 13, fontFamily: "system-ui", padding: "3px 0" }}>
                    <span style={{ width: 130, color: "#888", flexShrink: 0 }}>{k}</span>
                    <span style={{ color: "#1a1a1a" }}>{v}</span>
                  </div>
                ))}
              </div>
            ))}
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8b7355", fontWeight: 700, fontFamily: "system-ui", marginBottom: 4 }}>Documents</div>
            {openArt.sheet.documents.map((d) => (
              <div key={d} style={{ fontSize: 13, fontFamily: "system-ui", padding: "3px 0" }}>✓ {d}</div>
            ))}
            <p style={{ fontSize: 12, color: "#9ca3af", fontFamily: "system-ui", marginTop: 18 }}>
              Fiche fictive. Les estimations sont indicatives et ne remplacent pas une expertise.
            </p>
          </DemoPanel>
        )}
        {panel === "team" && (
          <DemoPanel title="Collaborateurs" onClose={() => setPanel(null)}>
            <p style={{ fontSize: 13, color: "#6b6b6b", fontFamily: "system-ui", marginTop: 0 }}>
              Vous invitez des Art Advisors et des utilisateurs Family Office, avec des droits de lecture seule ou de modification.
            </p>
            {TEAM.map((m) => (
              <div key={m.name} style={{ padding: "12px 0", borderBottom: "1px solid #f0ece5", fontFamily: "system-ui" }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{m.name}</div>
                <div style={{ fontSize: 12, color: "#888" }}>{m.role}</div>
                <div style={{ fontSize: 12, color: "#1c3a5e", marginTop: 2 }}>{m.access}</div>
              </div>
            ))}
            <p style={{ fontSize: 12, color: "#9ca3af", fontFamily: "system-ui", marginTop: 16 }}>Exemple fictif : dans votre compte, vous invitez vos propres collaborateurs.</p>
          </DemoPanel>
        )}
        {panel === "report" && (
          <DemoPanel title="Rapport annuel au Conseil d'administration" onClose={() => setPanel(null)}>
            <p style={{ fontSize: 13, color: "#6b6b6b", fontFamily: "system-ui", marginTop: 0 }}>
              Le rapport est généré à partir de vos collections et se télécharge en PDF, Word ou Excel. Il contient :
            </p>
            <ul style={{ fontSize: 13, color: "#1a1a1a", fontFamily: "system-ui", lineHeight: 1.7, paddingLeft: 18 }}>
              {REPORT_SECTIONS.map((s) => <li key={s}>{s}</li>)}
            </ul>
            <p style={{ fontSize: 12, color: "#9ca3af", fontFamily: "system-ui", marginTop: 16 }}>Aperçu descriptif : aucun fichier n'est généré dans cette démonstration.</p>
          </DemoPanel>
        )}

        <p style={{ marginTop: 40, fontSize: 12, color: "#9ca3af", fontFamily: "system-ui" }}>
          Toutes les données de cette page sont fictives. {BRAND_NAME} organise l'information ; les estimations sont indicatives et ne remplacent ni une expertise ni un avis juridique.
        </p>
      </div>
    </div>
  );
}
